import { useEffect, useState } from "react";
import { Avatar, Button, Flex, Input, Pagination, Select, Space, Typography } from "antd";
import {
  TbAlertCircle,
  TbClock,
  TbFile,
  TbListCheck,
  TbLoader2,
  TbMail,
  TbMoneybag,
  TbSearch,
  TbSettings,
  TbShield,
  TbSortAscending,
  TbSortDescending,
  TbTrendingDown,
} from "react-icons/tb";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";
import { ApiService } from "api/api.service";
import { NotificationCategory, NotificationSeverity, T_Notification } from "dtos/notifications.dto";
import dayjs from "dayjs";
import ResolveEmailModal from "components/Notifications/ResolveEmailModal";
import { toCapitalCase } from "utils/functions";
import { useParams } from "react-router-dom";
import { T_PageTitle } from "store/pageTitleSlice";
import EmptyNotifications from "components/svgs/EmptyNotifications";

const notificationIconMap: Record<NotificationCategory, React.ReactNode> = {
  [NotificationCategory.APPLICATION]: <TbFile />,
  [NotificationCategory.SYSTEM]: <TbSettings />,
  [NotificationCategory.PAYMENT]: <TbMoneybag />,
  [NotificationCategory.SECURITY]: <TbShield />,
  [NotificationCategory.RISK]: <TbTrendingDown />,
  [NotificationCategory.EMAIL]: <TbMail />,
  [NotificationCategory.TASK]: <TbListCheck />,
};

type ColorProps = Record<"backgroundColor" | "color", string>;
const severityProps: Record<NotificationSeverity, ColorProps> = {
  info: { color: "#6f44ef", backgroundColor: "#f1edff" },
  success: { color: "#16a34a", backgroundColor: "#ecfdf3" },
  warning: { color: "#f59e0b", backgroundColor: "#fffbeb" },
  error: { color: "#ef4444", backgroundColor: "#fef2f2" },
};

const Notifications = () => {
  const { appKey } = useParams();
  const [searchText, setSearchText] = useState("");
  const [resolveModal, setResolveModal] = useState<{
    open: boolean;
    notification: T_Notification | null;
  }>({ open: false, notification: null });

  const [filters, setFilters] = useState<Parameters<typeof ApiService.notification.useGetNotifications>[0]>({
    page: 1,
    limit: 10,
    search: "",
    category: "all",
    actionableOnly: false,
    sortBy: "createdAt",
    sortOrder: -1,
    appKey,
  });

  useEffect(() => {
    const delay = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        search: searchText.trim() || "",
      }));
    }, 300);

    return () => clearTimeout(delay);
  }, [searchText]);

  const fullApplication = ApiService.application.useGetByAppKey(appKey);
  const breadcrumbs: T_PageTitle = [];
  if (appKey) {
    const name =
      fullApplication.data?.data?.application?.client_details?.name ??
      fullApplication.data?.application?.meta?.client_name;

    breadcrumbs.push(
      { title: "Applications", link: `/dashboard/applications` },
      {
        title: name ?? "Application Item",
        link: `/dashboard/application/${appKey}`,
      },
    );
  }

  breadcrumbs.push({ title: "Notifications" });

  const res = ApiService.notification.useGetNotifications(filters);
  const notificationData = res.data?.result.data ?? [];

  return (
    <DashboardLayout
    // headerRight={
    //   <Button loading={res.isFetching} icon={<TbRefresh />} type="primary" onClick={() => res.refetch()}>
    //     Refresh
    //   </Button>
    // }
    >
      <PageTitle title="Notifications" pageTitle={breadcrumbs} />

      <div className="p-4">
        <div className="flex justify-between gap-3">
          <div className="inline-flex justify-between gap-3 items-center w-full">
            <Flex gap={8}>
              <Input
                prefix={<TbSearch />}
                placeholder="Search notifications..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-80"
              />
              <Space.Compact>
                <Select
                  className="min-w-32"
                  value={filters.sortBy}
                  onChange={(value) => setFilters((prev) => ({ ...prev, sortBy: value }))}
                  options={[
                    {
                      label: (
                        <Space>
                          <TbClock className="inline" />
                          Notified at
                        </Space>
                      ),
                      value: "createdAt",
                    },
                    {
                      label: (
                        <Space>
                          <TbAlertCircle className="inline" />
                          Priority
                        </Space>
                      ),
                      value: "priority",
                    },
                  ]}
                />
                <Button
                  icon={filters.sortOrder === 1 ? <TbSortAscending /> : <TbSortDescending />}
                  onClick={() => setFilters((prev) => ({ ...prev, sortOrder: prev.sortOrder === 1 ? -1 : 1 }))}
                />
              </Space.Compact>
            </Flex>
            <Flex gap={8}>
              <Button
                type={filters.actionableOnly ? "primary" : "default"}
                onClick={() => setFilters((prev) => ({ ...prev, actionableOnly: !prev.actionableOnly }))}
              >
                Actionable Only
              </Button>
              <Select
                value={filters.category}
                onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
                className="min-w-40"
                options={[
                  {
                    label: (
                      <Space className="flex gap-1.5">
                        <Typography.Text>All Categories</Typography.Text>
                      </Space>
                    ),
                    value: "all",
                  },
                  ...Object.values(NotificationCategory).map((category) => ({
                    label: (
                      <Space className="flex gap-1.5">
                        {notificationIconMap[category]}
                        <Typography.Text>{toCapitalCase(category)}</Typography.Text>
                      </Space>
                    ),
                    value: category,
                  })),
                ]}
              />
            </Flex>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-6 relative">
          {res.isLoading ? (
            <div className="flex flex-col gap-4 justify-center items-center">
              <TbLoader2 className="animate-spin size-6" color="#6f44ef" />
              <Typography.Text>Loading...</Typography.Text>
            </div>
          ) : notificationData.length === 0 ? (
            <EmptyNotifications />
          ) : (
            notificationData.map((group, idx) => (
              <div key={idx} className="flex flex-col gap-2">
                <Typography.Title level={4}>{group.title}</Typography.Title>

                <div className="flex flex-col gap-2 flex-1">
                  {group.notification.map((_n: T_Notification) => (
                    <div
                      key={_n._id}
                      className="flex justify-between items-center gap-4 border border-gray-200 rounded-xl p-4 bg-white transition-all duration-200"
                    >
                      <div className="flex gap-4 items-center">
                        <Avatar icon={notificationIconMap[_n.category]} style={severityProps[_n.severity]} />

                        <div className="flex flex-col">
                          <Typography.Title level={5} className="!mb-1 !text-base !font-medium text-gray-900">
                            {_n.title}
                          </Typography.Title>

                          <Typography.Text className="text-gray-600 text-sm">{_n.message}</Typography.Text>

                          {_n.actions && _n.actions.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {/* "Fill & Send" button for pending email notifications */}
                              {_n.category === NotificationCategory.EMAIL && _n.context?.cardActivity ? (
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() =>
                                    setResolveModal({
                                      open: true,
                                      notification: _n,
                                    })
                                  }
                                >
                                  Fill & Send
                                </Button>
                              ) : (
                                _n.actions.map((action, i) => (
                                  <Button key={i} size="small" type="primary" onClick={() => window.open(action.url)}>
                                    {action.label}
                                  </Button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <Typography.Text type="secondary" className="text-xs text-gray-400 whitespace-nowrap">
                        {dayjs(_n.createdAt).fromNow()}
                      </Typography.Text>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {!res.isFetching && (
        <Pagination
          className="pr-4 pt-2"
          onChange={(page, pageSize) =>
            setFilters((prev) => ({
              ...prev,
              limit: pageSize,
              page,
            }))
          }
          hideOnSinglePage
          align="end"
          showSizeChanger
          defaultCurrent={filters.page}
          total={res.data?.result.totalObjects ?? 0}
          pageSize={filters.limit}
          current={filters.page}
          showTotal={(total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} results`;
          }}
        />
      )}
      <ResolveEmailModal
        open={resolveModal.open}
        notification={resolveModal.notification}
        onClose={() => setResolveModal({ open: false, notification: null })}
        onResolved={() => res.refetch()}
      />
    </DashboardLayout>
  );
};

export default Notifications;
